<script setup lang="ts">
import { onMounted, ref } from 'vue'

import { t } from '@satch-ui/translations/translations'
import { sizeImageUrl } from '@shared/fondof-fn/image/size-image'
import { getDataURL } from '@shared/fondof-fn/image/thumbhash'
import { useIsScreenSmMax } from '@shared/fondof-fn/media-query/screen'
import CarouselItem from '@shared/fondof-ui/components/molecules/carousel/CarouselItem.vue'
import CarouselRoot from '@shared/fondof-ui/components/molecules/carousel/CarouselRoot.vue'
import CarouselScrollContainer from '@shared/fondof-ui/components/molecules/carousel/CarouselScrollContainer.vue'
import CarouselViewport from '@shared/fondof-ui/components/molecules/carousel/CarouselViewport.vue'
import CarouselPagination from '@storefront-satch/components/molecules/emblaCarousel/CarouselPagination.vue'
import GalleryThumbnails from '@storefront-satch/components/molecules/gallery/GalleryThumbnails.vue'
import GalleryVideo from '@storefront-satch/components/molecules/gallery/GalleryVideo.vue'
import GalleryZoomModal from '@storefront-satch/components/molecules/gallery/GalleryZoomModal.vue'
import type { MediaItem } from '@storefront-satch/components/organisms/gallery/types'
import type { EmblaCarouselType } from 'embla-carousel'
import Fade from 'embla-carousel-fade'

import { PauseIcon, PlayIcon } from '@fond-of/satch-icons'

const props = defineProps<{
  media: MediaItem[]
  video?: string
}>()

const mainCarousel = ref<EmblaCarouselType | undefined>(undefined)

const thumbsCarousel = ref<EmblaCarouselType | undefined>(undefined)

const isZoomHoverActive = ref<boolean>(false)
const isThumbHoverActive = ref<boolean>(false)

const zoomImage = ref<HTMLImageElement | null>(null)
const zoomHoverContainer = ref<HTMLElement | null>(null)
const galleryImage = ref<HTMLImageElement[] | null>(null)
const zoomScale = ref<number>(3)

const xPosition = ref(0)
const yPosition = ref(0)
const xPositionLayer = ref(0)
const yPositionLayer = ref(0)

const videoIndex = ref<number>(1)
const activeIndex = ref<number>(0)
const isVideoPlaying = ref<boolean>(false)

const showZoomModal = ref<boolean>(false)

const imageSize = {
  md: 480,
  xl: 800,
  xxl: 1240,
  xxxl: 1600,
}

const setThumbsCarousel = (carousel: EmblaCarouselType | undefined) => {
  thumbsCarousel.value = carousel
}

const setMainCarousel = (carousel: EmblaCarouselType | undefined) => {
  mainCarousel.value = carousel
}

const isMobile = useIsScreenSmMax()

const setActiveIndex = (value: number) => {
  activeIndex.value = value
}

const setShowZoomModal = (value: boolean) => {
  showZoomModal.value = value
}

const setIsVideoPlaying = (value: boolean) => {
  isVideoPlaying.value = value
}

const setZoomHoverActive = (value: boolean) => {
  isZoomHoverActive.value = value
}

const triggerPausePlayVideo = () => {
  if (isVideoPlaying.value) {
    isVideoPlaying.value = false
  } else {
    isVideoPlaying.value = true

    mainCarousel.value?.scrollTo(videoIndex.value)
    thumbsCarousel.value?.scrollTo(videoIndex.value)
  }
}

const onZoomHover = (event: MouseEvent) => {
  setZoomHoverActive(true)

  if (
    mainCarousel.value &&
    galleryImage.value &&
    zoomHoverContainer.value &&
    zoomImage.value
  ) {
    const zoomContainerHalfWidth = zoomHoverContainer.value?.clientWidth / 2
    const zoomContainerHalfHeight = zoomHoverContainer.value?.clientHeight / 2

    xPosition.value = event.offsetX - zoomContainerHalfWidth
    yPosition.value = event.offsetY - zoomContainerHalfHeight

    xPositionLayer.value = -(
      event.offsetX *
        ((zoomImage.value?.width * zoomScale.value) /
          galleryImage.value?.[0].width) -
      zoomImage.value?.width / 2
    )

    yPositionLayer.value = -(
      event.offsetY *
        ((zoomImage.value?.height * zoomScale.value) /
          galleryImage.value?.[0].height) -
      zoomImage.value?.height / 2
    )
  }
}

const onHoverThumbStart = (index: number) => {
  isThumbHoverActive.value = true

  mainCarousel.value?.scrollTo(index)
}

const onHoverThumbEnd = () => {
  isThumbHoverActive.value = false

  mainCarousel.value?.scrollTo(activeIndex.value)
}

const onSlideChange = (carousel: EmblaCarouselType | undefined) => {
  if (!carousel) return

  const newIndex = carousel.selectedScrollSnap()

  if (!isThumbHoverActive.value) {
    setActiveIndex(newIndex)
  }

  const currentMedia = props.media[newIndex]
  if (currentMedia?.mediaType === 'video') {
    setIsVideoPlaying(true)
  } else {
    setIsVideoPlaying(false)
  }

  if (mainCarousel.value?.selectedScrollSnap() === newIndex) {
    return
  }

  mainCarousel.value?.scrollTo(newIndex)
}

const imageLoaded = ref(false)
const isMounted = ref(false)

const handleImageLoad = () => {
  imageLoaded.value = true
}

// native @load event can fire before Vue hydration completes
// checking img state onMounted, we make sure the loading state is updated
onMounted(() => {
  isMounted.value = true
  if (galleryImage.value && galleryImage.value[0]?.complete) {
    handleImageLoad()
  }
})
</script>

<template>
  <div
    id="product-view__media"
    class="md:justify-left relative w-full sm:pl-0 sm:pr-6 md:flex md:h-full md:flex-wrap"
  >
    <div
      class="relative flex w-full max-w-fit flex-wrap overflow-hidden sm:gap-5 sm:overflow-visible md:gap-10 md:pb-[40px]"
    >
      <div
        v-if="!isMobile"
        class="hidden w-full md:block md:w-auto"
      >
        <span
          v-if="props.video"
          class="text-primary font-sans-semi-bold mb-2.5 flex w-[100px] cursor-pointer items-center justify-center whitespace-nowrap text-center text-[11px]"
          @click="triggerPausePlayVideo"
        >
          {{ isVideoPlaying ? t('video.pauseVideo') : t('video.playVideo') }}
          <component
            :is="isVideoPlaying ? PauseIcon : PlayIcon"
            class="ml-1.5 size-4 min-w-4"
          />
        </span>
        <!-- thumbnail -->
        <GalleryThumbnails
          v-if="props.media.length"
          thumbnail-height="100px"
          thumbnail-width="100px"
          space-between="10px"
          :slides-to-scroll="1"
          :media="props.media"
          axis="y"
          :active-index="activeIndex"
          thumbnail-active-class="opacity-100 border-2 border-primary"
          scroll-container-class="h-[585px]"
          @start-hover="onHoverThumbStart"
          @end-hover="onHoverThumbEnd"
          @clicked="setActiveIndex"
          @embla-api="setThumbsCarousel"
        />
        <!-- thumbnailEnd -->
      </div>
      <!-- media -->
      <div
        class="relative order-1 mb-4 w-full md:mb-0 md:h-auto md:w-[calc(100%-140px)]"
      >
        <!-- slider -->
        <div class="relative w-full lg:mb-0 lg:h-auto">
          <CarouselRoot
            v-if="props.media.length"
            :options="{
              breakpoints: {
                '(min-width: 960px)': { watchDrag: false },
              },
              loop: true,
              pagination: true,
              watchDrag: true,
            }"
            :modules="[
              Fade({
                active: false,
                breakpoints: {
                  '(min-width: 960px)': { active: true },
                },
              }),
            ]"
            @embla-api="setMainCarousel"
            @active-index-change="onSlideChange"
          >
            <CarouselViewport>
              <CarouselScrollContainer>
                <CarouselItem
                  v-for="(item, index) in props.media"
                  :key="index"
                  class="relative size-full"
                >
                  <div class="relative overflow-hidden">
                    <picture
                      v-if="item.mediaType === 'image'"
                      data-chromatic="ignore"
                    >
                      <source
                        :srcset="`${sizeImageUrl(item.url, imageSize.md)} 1x, ${sizeImageUrl(item.url, imageSize.xl)} 2x`"
                        media="(max-width: 480px)"
                      />
                      <source
                        :srcset="`${sizeImageUrl(item.url, imageSize.xl)} 1x, ${sizeImageUrl(item.url, imageSize.xxxl)} 2x`"
                        media="(max-width: 768px)"
                      />
                      <source
                        :srcset="`${sizeImageUrl(item.url, imageSize.md)} 1x, ${sizeImageUrl(item.url, imageSize.xxl)} 2x`"
                        media="(max-width: 960px)"
                      />
                      <source
                        :srcset="`${sizeImageUrl(item.url, imageSize.xl)} 1x, ${sizeImageUrl(item.url, imageSize.xxl)} 2x`"
                      />
                      <img
                        ref="galleryImage"
                        class="z-10 aspect-square size-full object-contain lg:cursor-none"
                        :src="`${item.url}${imageSize.xxl}`"
                        :loading="index > 0 ? 'lazy' : 'eager'"
                        :fetchpriority="index < 1 ? 'high' : undefined"
                        :class="[{ 'opacity-0': !isMounted || !imageLoaded }]"
                        @click.prevent="isMobile && setShowZoomModal(true)"
                        @mouseenter="!isMobile && onZoomHover($event)"
                        @mousemove="!isMobile && onZoomHover($event)"
                        @mouseleave="!isMobile && setZoomHoverActive(false)"
                        @load="handleImageLoad"
                      />
                    </picture>

                    <img
                      v-if="item.thumbhash"
                      class="pointer-events-none absolute inset-0 aspect-square size-full scale-75 object-cover blur-md transition-opacity"
                      :class="[{ 'opacity-0': imageLoaded }]"
                      :src="getDataURL(item.thumbhash)"
                      aria-hidden="true"
                    />

                    <GalleryVideo
                      v-if="item.mediaType === 'video'"
                      video-class="max-w-full"
                      video-preload="metadata"
                      :item="item"
                      :is-active="
                        showZoomModal
                          ? false
                          : index === activeIndex && item.mediaType === 'video'
                      "
                      :play="isVideoPlaying"
                      @play="setIsVideoPlaying(true)"
                      @pause="setIsVideoPlaying(false)"
                    />
                  </div>
                </CarouselItem>
              </CarouselScrollContainer>
            </CarouselViewport>

            <CarouselPagination
              v-if="isMobile && props.media.length > 1"
              class="bottom-2.5"
              :class="{
                'carousel-pagination-with-video': props.video,
              }"
            />
          </CarouselRoot>
          <div
            v-show="isZoomHoverActive"
            ref="zoomHoverContainer"
            class="outline-primary pointer-events-none absolute left-0 top-0 z-10 flex origin-center translate-x-full transform flex-wrap items-center justify-center overflow-hidden bg-white outline outline-2 lg:size-[300px] xl:size-[400px]"
            :style="`transform: translate(${xPosition}px, ${yPosition}px)`"
          >
            <img
              v-if="!isMobile"
              ref="zoomImage"
              :src="`${props.media[activeIndex].url}${imageSize.xxl}`"
              :srcset="`${props.media[activeIndex].url}${imageSize.xxl} 1x, ${props.media[activeIndex].url}${imageSize.xxxl} 2x`"
              class="size-full max-w-none origin-top-left transform object-contain"
              :style="`transform: translate(${xPositionLayer}px, ${yPositionLayer}px) scale(${zoomScale})`"
            />
          </div>
        </div>
        <!-- sliderEnd -->
        <div class="text-gray-medium text-2xs w-full text-center">
          {{ t('productDetail.imageHint') }}
        </div>
      </div>
      <!-- mediaEnd -->
    </div>
  </div>

  <GalleryZoomModal
    v-if="isMounted && isMobile"
    :is-open="showZoomModal"
    :active-index="activeIndex"
    :media="props.media"
    class="z-zoom-modal"
    @close="setShowZoomModal(false)"
    @on-slide-change="onSlideChange"
  />
</template>

<style lang="scss" scoped>
.carousel-pagination-with-video :deep(.active:nth-child(2)),
.carousel-pagination-with-video :deep(.inactive:nth-child(2)) {
  position: relative;
  background-color: transparent;
  border-width: 0;
}

.carousel-pagination-with-video :deep(.inactive:nth-child(2):before) {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd" stroke-linecap="round" stroke-linejoin="round"><polygon stroke="%23000" stroke-width="2" points="4 1 13 8.0321381 4 15"></polygon></g></svg>')
    no-repeat center center transparent;
  background-size: 100%;
  pointer-events: none;
}

.carousel-pagination-with-video :deep(.active:nth-child(2):before) {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 16px;
  height: 16px;
  background: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><polygon stroke="%23000" stroke-width="2" points="4 1 13 8.0321381 4 15"></polygon></svg>');
  background-size: 100%;
  pointer-events: none;
}
</style>
